import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, KeyRound, Trash2 } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import type {
  AiProviderCredentialsStatusResponse,
  SystemSetting,
  SystemSettingResponse,
} from '@sdr-agend/admin-schemas'

/**
 * `AiProviderSettings` — 2026-07-27 (user request): let the operator choose
 * which AI provider (OpenAI / Gemini / Claude) powers `chatCompletion` calls
 * and type in each provider's API key directly in the admin UI. Deliberately
 * NOT part of the generic `SettingEditor` flow (Settings.tsx): API keys are
 * secrets (never round-tripped back to the browser), and the 2 concerns
 * (which provider is active + whether its key is saved) are coupled enough
 * that a dedicated card reads clearer than 3 generic setting rows.
 *
 * Admin-only — mirrors the backend's RBAC on both
 * `/admin/ai-provider-credentials` and the `ai_provider_*` system_settings.
 */

const PROVIDER_LABELS: Record<'openai' | 'gemini' | 'claude', string> = {
  openai: 'OpenAI',
  gemini: 'Gemini (Google)',
  claude: 'Claude (Anthropic)',
}

const CLAUDE_MODEL_OPTIONS = ['claude-sonnet-5', 'claude-opus-4-8', 'claude-haiku-4-5'] as const
const GEMINI_MODEL_OPTIONS = ['gemini-2.0-flash', 'gemini-1.5-pro'] as const

function findSettingValue(settings: SystemSetting[], key: string): unknown {
  return settings.find((s) => s.key === key)?.value
}

export function AiProviderSettings({ settings }: { settings: SystemSetting[] }): React.ReactElement {
  const queryClient = useQueryClient()
  const [keyDrafts, setKeyDrafts] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})

  const activeProvider = (findSettingValue(settings, 'ai_provider_active') as string | undefined) ?? 'openai'
  const claudeModel =
    (findSettingValue(settings, 'ai_provider_claude_model') as string | undefined) ?? 'claude-sonnet-5'
  const geminiModel =
    (findSettingValue(settings, 'ai_provider_gemini_model') as string | undefined) ?? 'gemini-2.0-flash'

  const { data: credStatus } = useQuery<AiProviderCredentialsStatusResponse>({
    queryKey: ['ai-provider-credentials'],
    queryFn: async () => api<AiProviderCredentialsStatusResponse>('/admin/ai-provider-credentials'),
  })

  function invalidateAll(): void {
    void queryClient.invalidateQueries({ queryKey: ['admin-system-settings'] })
    void queryClient.invalidateQueries({ queryKey: ['ai-provider-credentials'] })
  }

  const settingMutation = useMutation({
    mutationFn: async (input: { key: string; value: string; rationale: string }) =>
      api<SystemSettingResponse>(`/admin/system-settings/${input.key}`, {
        method: 'PATCH',
        body: JSON.stringify({ value: input.value, rationale: input.rationale }),
      }),
    onSuccess: invalidateAll,
  })

  const setKeyMutation = useMutation({
    mutationFn: async (input: { provider: string; apiKey: string }) =>
      api(`/admin/ai-provider-credentials/${input.provider}`, {
        method: 'PUT',
        body: JSON.stringify({ apiKey: input.apiKey }),
      }),
    onSuccess: (_res, input) => {
      setKeyDrafts((prev) => ({ ...prev, [input.provider]: '' }))
      setErrors((prev) => ({ ...prev, [input.provider]: '' }))
      invalidateAll()
    },
    onError: (err: Error, input) => {
      const apiErr = err instanceof ApiError ? err : null
      const body = apiErr?.bodyJson as { error?: { message?: string } } | null
      setErrors((prev) => ({ ...prev, [input.provider]: body?.error?.message ?? err.message }))
    },
  })

  const deleteKeyMutation = useMutation({
    mutationFn: async (provider: string) =>
      api(`/admin/ai-provider-credentials/${provider}`, { method: 'DELETE' }),
    onSuccess: invalidateAll,
  })

  const providers = credStatus?.providers

  return (
    <Card className="p-4">
      <h2 className="mb-1 text-base font-semibold">Provedor de IA</h2>
      <p className="mb-4 text-xs text-muted-foreground">
        Escolha qual IA responde às conversas e cadastre a chave de acesso de cada provedor. A chave
        fica criptografada no banco e nunca é exibida de volta aqui.
      </p>

      <div className="mb-4 grid gap-1.5">
        <Label htmlFor="ai-provider-active">IA ativa</Label>
        <Select
          id="ai-provider-active"
          value={activeProvider}
          disabled={settingMutation.isPending}
          onChange={(e) => {
            const value = e.target.value
            settingMutation.mutate({
              key: 'ai_provider_active',
              value,
              rationale: `Provedor de IA alterado para ${PROVIDER_LABELS[value as keyof typeof PROVIDER_LABELS]} via painel de configurações`,
            })
          }}
        >
          <option value="openai">OpenAI</option>
          <option value="gemini">Gemini (Google)</option>
          <option value="claude">Claude (Anthropic)</option>
        </Select>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <ProviderKeyCard
          provider="openai"
          configured={providers?.openai ?? false}
          draft={keyDrafts.openai ?? ''}
          onDraftChange={(v) => setKeyDrafts((prev) => ({ ...prev, openai: v }))}
          error={errors.openai}
          saving={setKeyMutation.isPending && setKeyMutation.variables?.provider === 'openai'}
          deleting={deleteKeyMutation.isPending && deleteKeyMutation.variables === 'openai'}
          onSave={() => setKeyMutation.mutate({ provider: 'openai', apiKey: keyDrafts.openai ?? '' })}
          onDelete={() => deleteKeyMutation.mutate('openai')}
        />
        <ProviderKeyCard
          provider="gemini"
          configured={providers?.gemini ?? false}
          draft={keyDrafts.gemini ?? ''}
          onDraftChange={(v) => setKeyDrafts((prev) => ({ ...prev, gemini: v }))}
          error={errors.gemini}
          saving={setKeyMutation.isPending && setKeyMutation.variables?.provider === 'gemini'}
          deleting={deleteKeyMutation.isPending && deleteKeyMutation.variables === 'gemini'}
          onSave={() => setKeyMutation.mutate({ provider: 'gemini', apiKey: keyDrafts.gemini ?? '' })}
          onDelete={() => deleteKeyMutation.mutate('gemini')}
          modelValue={geminiModel}
          modelOptions={GEMINI_MODEL_OPTIONS}
          onModelChange={(value) =>
            settingMutation.mutate({
              key: 'ai_provider_gemini_model',
              value,
              rationale: `Modelo Gemini alterado para ${value} via painel de configurações`,
            })
          }
        />
        <ProviderKeyCard
          provider="claude"
          configured={providers?.claude ?? false}
          draft={keyDrafts.claude ?? ''}
          onDraftChange={(v) => setKeyDrafts((prev) => ({ ...prev, claude: v }))}
          error={errors.claude}
          saving={setKeyMutation.isPending && setKeyMutation.variables?.provider === 'claude'}
          deleting={deleteKeyMutation.isPending && deleteKeyMutation.variables === 'claude'}
          onSave={() => setKeyMutation.mutate({ provider: 'claude', apiKey: keyDrafts.claude ?? '' })}
          onDelete={() => deleteKeyMutation.mutate('claude')}
          modelValue={claudeModel}
          modelOptions={CLAUDE_MODEL_OPTIONS}
          onModelChange={(value) =>
            settingMutation.mutate({
              key: 'ai_provider_claude_model',
              value,
              rationale: `Modelo Claude alterado para ${value} via painel de configurações`,
            })
          }
        />
      </div>
    </Card>
  )
}

interface ProviderKeyCardProps {
  provider: 'openai' | 'gemini' | 'claude'
  configured: boolean
  draft: string
  onDraftChange: (value: string) => void
  error: string | undefined
  saving: boolean
  deleting: boolean
  onSave: () => void
  onDelete: () => void
  modelValue?: string
  modelOptions?: ReadonlyArray<string>
  onModelChange?: (value: string) => void
}

function ProviderKeyCard({
  provider,
  configured,
  draft,
  onDraftChange,
  error,
  saving,
  deleting,
  onSave,
  onDelete,
  modelValue,
  modelOptions,
  onModelChange,
}: ProviderKeyCardProps): React.ReactElement {
  return (
    <div className="rounded-md border p-3" data-testid={`ai-provider-card-${provider}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{PROVIDER_LABELS[provider]}</span>
        {configured ? (
          <Badge variant="success">
            <Check className="mr-1 h-3 w-3" aria-hidden="true" />
            Configurado
          </Badge>
        ) : (
          <Badge variant="secondary">Não configurado</Badge>
        )}
      </div>

      {modelOptions !== undefined && onModelChange !== undefined && (
        <div className="mb-2 grid gap-1">
          <Label htmlFor={`ai-model-${provider}`} className="text-xs text-muted-foreground">
            Modelo
          </Label>
          <Select
            id={`ai-model-${provider}`}
            value={modelValue}
            onChange={(e) => onModelChange(e.target.value)}
          >
            {modelOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </div>
      )}

      {provider === 'openai' ? (
        // 2026-07-28 fix — a chave da OpenAI sempre viveu na variável de
        // ambiente do servidor (OPENAI_API_KEY), nunca em
        // ai_provider_credentials — createAiService's openai branch não lê
        // essa tabela. Um input aqui salvaria uma chave que nunca seria
        // usada. Mostramos só o status (via env), sem controle editável.
        <p className="text-xs text-muted-foreground">
          Chave configurada na variável de ambiente do servidor (
          <code className="rounded bg-muted px-1 py-0.5 font-mono">OPENAI_API_KEY</code>) — não
          editável por aqui.
        </p>
      ) : (
        <>
          <div className="grid gap-1">
            <Label htmlFor={`ai-key-${provider}`} className="text-xs text-muted-foreground">
              Chave de API
            </Label>
            <Input
              id={`ai-key-${provider}`}
              type="password"
              autoComplete="off"
              placeholder={configured ? '•••••••••••••••• (salva)' : 'cole a chave aqui'}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
            />
          </div>
          {error !== undefined && error.length > 0 && (
            <p className="mt-1 text-xs text-destructive">{error}</p>
          )}
        </>
      )}

      {provider !== 'openai' && (
        <div className="mt-2 flex gap-2">
          <Button
            type="button"
            size="sm"
            disabled={draft.length === 0 || saving}
            onClick={onSave}
          >
            <KeyRound className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            {saving ? 'Salvando…' : 'Salvar chave'}
          </Button>
          {configured && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={deleting}
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              {deleting ? 'Removendo…' : 'Remover'}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default AiProviderSettings
